import type { SeriesOptionsType } from "highcharts";
import type { ChartSorting } from "@reporting/rp-types/analytics";

/**
 * Sort the categories of a chart by the series values - aggregated over all
 * the series or taken from the series of the given index - reordering the
 * data of every series the same way so the points keep their category. The
 * limit then renders only the first N categories (top N with a descending
 * order). Only the chart is sorted: the report grid keeps the data order,
 * the view options reference its cells (=A1). Missing points count as zero.
 */
export const sortChartData = (
	categories: string[],
	series: SeriesOptionsType[],
	sorting: ChartSorting
): {categories: string[], series: SeriesOptionsType[]} => {
	const {order, by, limit} = sorting ?? {};
	const sorted = order === 'asc' || order === 'desc';
	const limited = limit > 0 && limit < categories.length;
	if (!sorted && !limited) return {categories, series};

	const value = (serie: SeriesOptionsType, i: number): number =>
		Number((serie as any)?.data?.[i]?.y ?? 0);

	const key = (i: number): number => {
		if (typeof by === 'number') return value(series[by], i);
		const values = series.map(serie => value(serie, i));
		if (!values.length) return 0;
		switch (by) {
			case 'min': return Math.min(...values);
			case 'max': return Math.max(...values);
			case 'avg': return values.reduce((sum, item) => sum + item, 0) / values.length;
			default: return values.reduce((sum, item) => sum + item, 0);
		}
	};

	const keys = categories.map((_, i) => key(i));
	let indexes = categories.map((_, i) => i);
	if (sorted) indexes.sort((a, b) => order === 'desc' ? keys[b] - keys[a] : keys[a] - keys[b]);
	if (limited) indexes = indexes.slice(0, limit);

	return {
		categories: indexes.map(i => categories[i]),
		series: series.map(serie => ({
			...serie,
			data: indexes.map(i => (serie as any).data?.[i] ?? null),
		} as SeriesOptionsType)),
	};
};
